import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { EmailAddressSection } from '../EmailAddressSection/EmailAddressSection';
import { ProfilePageProps } from '../ProfilePage';
import { addEmail } from '../requests';
import { AddEmailModal, CloseButton } from '../AddEmailModal/AddEmailModal';
import {
  Button,
  createToast as createToastBase,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Strong,
  Toast,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

const createToast = (component: any) => {
  toast.dismiss();
  createToastBase(component);
};

const RemoveEmailModal = (props: {
  email: string | null;
  onClose: () => void;
  onRemove: () => void;
}) => {
  const { email, onClose, onRemove } = props;
  return (
    <Modal isOpen={!!email}>
      <Inline alignX="spaceBetween" alignY="baseline">
        <ModalHeader>Remove email?</ModalHeader>
        <CloseButton onClose={onClose} />
      </Inline>
      <ModalBody>
        <Stack spacing={6}>
          <div>
            You&apos;re about to remove the email <Strong>{email}</Strong>
          </div>
          <div>Are you sure you want to remove this email?</div>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onClose();
          }}
          variant="outline"
        >
          Cancel
        </Button>
        <Button type={'danger'} onClick={onRemove}>
          Remove email
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export type EmailsProps = {
  emails: ProfilePageProps['emails'];
  removeEmail: (email: string) => Promise<any>;
  makePrimary: (email: string) => Promise<any>;
  resendVerification: (email: string) => Promise<any>;
};

export const Emails = (props: EmailsProps) => {
  const {
    emails: emailProp,
    removeEmail,
    makePrimary,
    resendVerification,
  } = props;

  const [emails, setEmails] = useState(emailProp);
  const [emailToRemove, setEmailToRemove] = useState<string | null>(null);

  const [showAddEmailModal, setShowAddEmailModal] = useState(false);

  const [addEmailError, setAddEmailError] = useState(null);

  return (
    <>
      <AddEmailModal
        error={addEmailError}
        isOpen={showAddEmailModal}
        onClose={() => {
          setShowAddEmailModal(false);
          setAddEmailError(null);
        }}
        onSubmit={(email) => {
          setAddEmailError(null);
          return addEmail(email)
            .then(() => {
              setShowAddEmailModal(false);
              setEmails((prev) => {
                return [
                  ...prev,
                  {
                    email,
                    primary: false,
                    created_at: new Date().toISOString(),
                  },
                ];
              });

              createToast(
                <Toast type="success">
                  Email {email} has been added.
                  <br /> Check your inbox to verify your email.
                </Toast>
              );
            })
            .catch((response) => {
              // About to be redirected to enforced SSO login; re-reject promise so modal can handle state properly
              if (response.redirect) return Promise.reject(response);

              setAddEmailError(response.error.type);
            });
        }}
      />
      <RemoveEmailModal
        email={emailToRemove}
        onClose={() => {
          setEmailToRemove(null);
        }}
        onRemove={() => {
          if (!emailToRemove) {
            return;
          }
          removeEmail(emailToRemove)
            .then(() => {
              setEmails((emails) => {
                return emails.filter((email) => email.email !== emailToRemove);
              });
              setEmailToRemove(null);
              createToast(
                <Toast type="success">
                  Email: {emailToRemove} has been removed
                </Toast>
              );
            })
            .catch(() => {
              createToast(
                <Toast type="danger">Error trying to remove email</Toast>
              );
              setEmailToRemove(null);
            });
        }}
      />
      <EmailAddressSection
        emails={emails}
        makePrimary={(email) => {
          return makePrimary(email)
            .then(() => {
              setEmails((prev) => {
                return prev.map((emailObj) => {
                  emailObj.primary = emailObj.email === email;
                  return emailObj;
                });
              });

              createToast(
                <Toast type="success">{email} is now the primary email</Toast>
              );
            })
            .catch(() => {
              createToast(
                <Toast type="danger">Error trying to update email</Toast>
              );
            });
        }}
        removeEmail={(emailToRemove) => {
          setEmailToRemove(emailToRemove);
        }}
        resendVerification={(email) => {
          return resendVerification(email)
            .then(() => {
              createToast(
                <Toast type="success">
                  Sent verification request for: {email}
                </Toast>
              );
            })
            .catch(() => {
              createToast(
                <Toast type="danger">
                  Error trying to send verification request for: {email}
                </Toast>
              );
            });
        }}
        addEmail={() => {
          setShowAddEmailModal(true);
        }}
      />
    </>
  );
};
